import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { authService, profileService } from "../services/api.service";
import axiosInstance from "../utils/axios.config";
import { CrownOutlined } from "@ant-design/icons";
import logger from "../utils/logger";

const Sidebar = ({ onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [badgeCounts, setBadgeCounts] = useState({
    dashboard: 0,
    cases: 0,
  });

  useEffect(() => {
    fetchBadgeCounts();
  }, []);

  const fetchBadgeCounts = async () => {
    try {
      // استخدام طريقة أكثر أماناً لتجنب الأخطاء
      let dashboardCount = 0;
      let casesCount = 0;

      try {
        const dashboardResponse = await axiosInstance.get(
          "/dashboard/notifications-count"
        );
        dashboardCount = dashboardResponse.data?.count || 0;
      } catch (error) {
        logger.warn("Failed to fetch dashboard count, using default value");
      }

      try {
        // هناك مشكلة في هذا المسار، لذلك سنستخدم قيمة ثابتة مؤقتاً
        // const casesResponse = await axiosInstance.get('/cases/active-count');
        // casesCount = casesResponse.data?.count || 0;

        // بدلاً من ذلك، سنستخدم رقماً عشوائياً للأغراض التجريبية
        casesCount = Math.floor(Math.random() * 3); // رقم عشوائي بين 0 و 2 للتجربة
      } catch (error) {
        logger.warn("Failed to fetch cases count, using default value");
      }

      setBadgeCounts({
        dashboard: dashboardCount,
        cases: casesCount,
      });
    } catch (error) {
      logger.error("Error in fetchBadgeCounts:", error);
      setBadgeCounts({ dashboard: 0, cases: 0 });
    }
  };

  const [userProfile, setUserProfile] = useState({
    name: "",
    role: "",
    avatar: "/images/avatar.svg",
    office_name: "",
    email: "",
    phone: "",
  });

  // جلب بيانات المستخدم
  useEffect(() => {
    fetchUserProfile();
  }, []);

  const fetchUserProfile = async () => {
    try {
      const response = await profileService.getProfile();
      logger.debug("📥 Sidebar - Profile response:", response);

      if (response && response.success && response.data) {
        const profileData = response.data;

        // معالجة مسار الصورة
        let avatarPath = "/images/avatar.svg"; // القيمة الافتراضية

        // الحصول على عنوان الخادم بشكل ديناميكي
        const getServerUrl = () => {
          // في بيئة التطوير
          if (process.env.NODE_ENV === 'development') {
            return 'http://localhost:5000';
          }
          // في بيئة الإنتاج، استخدم نفس نطاق التطبيق
          return window.location.origin;
        };

        const serverUrl = getServerUrl();

        if (profileData.avatar_url) {
          // إذا كان المسار يبدأ بـ http أو https، استخدمه مباشرة
          if (profileData.avatar_url.startsWith("http")) {
            avatarPath = profileData.avatar_url;
          } else {
            // إذا كان مسار نسبي، أضف URL الخادم
            avatarPath = `${serverUrl}${
              profileData.avatar_url.startsWith("/") ? "" : "/"
            }${profileData.avatar_url}`;
          }
        } else if (profileData.profile_avatar) {
          if (profileData.profile_avatar.startsWith("http")) {
            avatarPath = profileData.profile_avatar;
          } else {
            avatarPath = `${serverUrl}${
              profileData.profile_avatar.startsWith("/") ? "" : "/"
            }${profileData.profile_avatar}`;
          }
        }

        setUserProfile({
          name: profileData.name || "المستخدم",
          role:
            profileData.role === "admin"
              ? "المدير"
              : profileData.role === "lawyer"
              ? "محامي"
              : profileData.role === "employee"
              ? "موظف"
              : "مستخدم",
          avatar: avatarPath,
          office_name: profileData.office_name || "",
          email: profileData.email || "",
          phone: profileData.phone || "",
        });
        logger.info("✅ Sidebar - Profile updated:", profileData.name);
        logger.debug("🖼️  Sidebar - Avatar path:", avatarPath);
      }
    } catch (error) {
      logger.error("❌ Sidebar - Error fetching profile:", error);
      // الحفاظ على القيم الافتراضية في حالة الفشل
    }
  };

  const menuItems = [
    {
      title: "لوحة التحكم",
      icon: "🏠",
      path: "/dashboard",
      badge:
        badgeCounts.dashboard > 0 ? badgeCounts.dashboard.toString() : null,
    },
    {
      title: "القضايا",
      icon: "⚖️",
      path: "/cases",
      // badge: badgeCounts.cases > 0 ? badgeCounts.cases.toString() : null
    },
    { title: "العملاء", icon: "👥", path: "/clients" },
    { title: "الخصوم", icon: "⚔️", path: "/opponents" },
    { title: "المدفوعات", icon: "💰", path: "/payments" },
    { title: "الإشعارات", icon: "🔔", path: "/notifications" },
    { title: "التقويم", icon: "📅", path: "/calendar" },
    { title: "التنفيذ", icon: "⚡", path: "/execution" }, // تم تغيير الأيقونة وتصحيح المسار
    { title: "التقارير", icon: "📊", path: "/reports" },
    {
      title: "الاشتراكات",
      icon: <CrownOutlined className="text-xl" />,
      path: "/pricing",
    },
  ];

  const handleLogout = async () => {
    try {
      await authService.logout();
      navigate("/login");
    } catch (error) {
      logger.error("Logout failed:", error);
    }
  };

  return (
    <div className="bg-white h-screen w-72 flex flex-col overflow-hidden">
      {/* Mobile Close Button */}
      <button
        onClick={onClose}
        className="lg:hidden absolute top-4 left-4 p-2 hover:bg-gray-100 rounded-lg"
      >
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </button>

      {/* Profile Section - Simplified Design */}
      <div className="relative p-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white">
        <button
          onClick={() => setIsProfileOpen(!isProfileOpen)}
          className="w-full flex items-center gap-3"
        >
          <div className="relative">
            <img
              src={userProfile.avatar}
              alt="profile"
              className="w-12 h-12 rounded-full border-2 border-white shadow-md object-cover"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = "/images/avatar.svg";
              }}
            />
            <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-400 border-2 border-white rounded-full"></span>
          </div>
          <div className="flex-1 text-right">
            <h2 className="font-bold text-white truncate">
              {userProfile.name || "المستخدم"}
            </h2>
            <p className="text-xs text-blue-100">{userProfile.role}</p>
          </div>
          <svg
            className={`w-4 h-4 text-white transition-transform ${
              isProfileOpen ? "rotate-180" : ""
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>

        {/* Profile Dropdown */}
        {isProfileOpen && (
          <div className="absolute right-4 left-4 bg-white rounded-lg shadow-2xl border border-gray-100 overflow-hidden z-20 mt-2">
            <button
              onClick={() => {
                navigate("/profile");
                setIsProfileOpen(false);
              }}
              className="w-full text-right px-4 py-3 text-sm text-gray-700 hover:bg-blue-50 transition-colors flex items-center gap-2"
            >
              <span className="text-lg">🧑‍💼</span>
              <span>الملف الشخصي</span>
            </button>
            <button
              onClick={() => {
                navigate("/settings");
                setIsProfileOpen(false);
              }}
              className="w-full text-right px-4 py-3 text-sm text-gray-700 hover:bg-blue-50 transition-colors flex items-center gap-2"
            >
              <span className="text-lg">⚙️</span>
              <span>الإعدادات</span>
            </button>
            <hr className="border-gray-100" />
            <button
              onClick={handleLogout}
              className="w-full text-right px-4 py-3 text-sm text-red-600 hover:bg-red-50 transition-colors flex items-center gap-2"
            >
              <span className="text-lg">🚪</span>
              <span>تسجيل خروج</span>
            </button>
          </div>
        )}
      </div>

      {/* Navigation Menu - Touch Friendly */}
      <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
        {menuItems.map((item, index) => (
          <button
            key={index}
            onClick={() => {
              navigate(item.path);
              onClose?.(); // Close sidebar on mobile after navigation
            }}
            className={`
              w-full flex items-center justify-between p-4 rounded-xl
              transform transition-all duration-200
              active:scale-95 touch-action-manipulation
              ${
                location.pathname === item.path
                  ? "bg-blue-50 text-blue-600 shadow-sm"
                  : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
              }
            `}
          >
            <div className="flex items-center gap-3">
              <span className="text-xl">{item.icon}</span>
              <span className="font-medium">{item.title}</span>
            </div>
            {item.badge && (
              <span className="px-2 py-1 text-xs font-bold rounded-full bg-red-100 text-red-600">
                {item.badge}
              </span>
            )}
          </button>
        ))}
      </nav>

      {/* Bottom Actions - Mobile Safe Area */}
      <div className="p-4 border-t border-gray-100 safe-bottom">
        <button
          onClick={() => navigate("/help")}
          className="w-full p-3 text-gray-600 hover:bg-gray-50 rounded-xl flex items-center gap-2 justify-center"
        >
          <span>❔</span>
          <span>مركز المساعدة</span>
        </button>
      </div>
    </div>
  );
};

export default Sidebar;
